import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cva } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import React from "react";
import { cls, cn } from "@/lib/utils";

/*
 * NavigationMenuViewport
 */

const NavigationMenuViewport = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Viewport>
>(({ className, ...properties }, reference) => (
  <div className={cn("absolute left-0 top-full flex justify-center")}>
    <NavigationMenuPrimitive.Viewport
      className={cn(
        cls`
          origin-top-center
          relative
          mt-1.5
          h-(--radix-navigation-menu-viewport-height)
          w-full
          overflow-hidden
          rounded-md
          border
          bg-popover
          text-popover-foreground
          shadow-lg
          data-[state=open]:animate-in
          data-[state=closed]:animate-out
          data-[state=closed]:zoom-out-95
          data-[state=open]:zoom-in-90
          md:w-(--radix-navigation-menu-viewport-width)
        `,
        className,
      )}
      ref={reference}
      {...properties}
    />
  </div>
));

NavigationMenuViewport.displayName =
  NavigationMenuPrimitive.Viewport.displayName;

/*
 * NavigationMenu
 */

const NavigationMenu = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>
>(({ className, children, ...properties }, reference) => (
  <NavigationMenuPrimitive.Root
    ref={reference}
    className={cn(
      "relative z-10 flex max-w-max flex-1 items-center justify-center",
      className,
    )}
    {...properties}
  >
    {children}
    {/* <NavigationMenuViewport /> */}
  </NavigationMenuPrimitive.Root>
));

NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;

/*
 * NavigationMenuList
 */

const NavigationMenuList = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List>
>(({ className, ...properties }, reference) => (
  <NavigationMenuPrimitive.List
    ref={reference}
    className={cn(
      "group flex flex-1 list-none items-center justify-center space-x-1",
      className,
    )}
    {...properties}
  />
));

NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;

/*
 * NavigationMenuItem
 */

const NavigationMenuItem = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Item>
>(({ className, ...properties }, reference) => (
  <NavigationMenuPrimitive.Item
    ref={reference}
    className={cn("relative", className)}
    {...properties}
  />
));

NavigationMenuItem.displayName = NavigationMenuPrimitive.Item.displayName;

/*
 * NavigationMenuTrigger
 */

const navigationMenuTriggerStyle = cva(
  `group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-active:bg-accent/50 data-[state=open]:bg-accent/50`,
);

const NavigationMenuTrigger = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Trigger>
>(({ className, children, ...properties }, reference) => (
  <NavigationMenuPrimitive.Trigger
    ref={reference}
    className={cn(navigationMenuTriggerStyle(), "group", className)}
    {...properties}
  >
    {children}{" "}
    <ChevronDown
      className={cn(
        `relative top-px ml-1 h-3 w-3 transition duration-200 group-data-[state=open]:rotate-180`,
      )}
      aria-hidden="true"
    />
  </NavigationMenuPrimitive.Trigger>
));

NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;

/*
 * NavigationMenuContent
 */

const NavigationMenuContent = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content>
>(({ className, ...properties }, reference) => (
  <NavigationMenuPrimitive.Content
    ref={reference}
    className={cn(
      cls`
        left-0
        absolute
        top-full
        w-fit
        bg-popover
        mt-[5px]
        md:w-auto
        data-[motion^=from-]:animate-in
        data-[motion^=to-]:animate-out
        data-[motion^=from-]:fade-in
        data-[motion^=to-]:fade-out
        data-[motion=from-end]:slide-in-from-right-52
        data-[motion=from-start]:slide-in-from-left-52
        data-[motion=to-end]:slide-out-to-right-52
        data-[motion=to-start]:slide-out-to-left-52
        origin-top-center
        overflow-hidden
        rounded-md
        border
        text-popover-foreground
        shadow-lg
        data-[state=open]:animate-in
        data-[state=closed]:animate-out
        data-[state=closed]:zoom-out-95
        data-[state=open]:zoom-in-90
      `,
      className,
    )}
    {...properties}
  />
));

NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;

/*
 * NavigationMenuLink
 */

const NavigationMenuLink = NavigationMenuPrimitive.Link;

/*
 * NavigationMenuIndicator
 */

const NavigationMenuIndicator = React.forwardRef<
  React.ComponentRef<typeof NavigationMenuPrimitive.Indicator>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Indicator>
>(({ className, ...properties }, reference) => (
  <NavigationMenuPrimitive.Indicator
    ref={reference}
    className={cn(
      `top-full z-1 flex h-1.5 items-end justify-center overflow-hidden data-[state=visible]:animate-in data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in`,
      className,
    )}
    {...properties}
  >
    <div
      className={cn(
        `relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-border shadow-md`,
      )}
    />
  </NavigationMenuPrimitive.Indicator>
));

NavigationMenuIndicator.displayName =
  NavigationMenuPrimitive.Indicator.displayName;

export {
  navigationMenuTriggerStyle,
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuContent,
  NavigationMenuTrigger,
  NavigationMenuLink,
  NavigationMenuIndicator,
  NavigationMenuViewport,
};
